import {
  BNBox,
  BNCard,
  BNDonutChart,
  BNInput,
  BNRichText,
  Culture,
  RegionId,
  currencyFormatter,
  numberFormatter,
} from "@bn-monorepo/js-packages";
import { Grid } from "@mui/material";
import { CardContent, CardHeader, InputAdornment, Paper, useTheme } from "@mui/material";
import { NumberFormatValues, NumericFormat } from "react-number-format";
import React, { useContext, useEffect, useState } from "react";
import Slider, { ISlider } from "../../Slider/Slider";
import SliderWithInput, { SliderWithInputSizes } from "../../Slider/SliderWithInput";

import LoanCalculatorSectionWrapper from "./LoanCalculatorSectionWrapper";
import Media from "../../../../../CmsComponents/Media/Media";
import RepaymentPlan from "./RepaymentPlan";
import RichText from "../../../../../CmsComponents/RichText/RichText";
import SectionWrapperImage from "../../Section/SectionWrapperImage";
import SectionWrapperText from "../../Section/SectionWrapperText";
import { SettingsContext } from "../../../../../../settingsContext";
import { StringContext } from "../../../../../../StringContext";
import {
  CalculateLoanResult,
  LoanParams,
  LoanType,
  calculateLoan,
} from "@bn-monorepo/js-packages/src/Helpers/calculators/calculateLoan";
import styles from "./LoanCalculator.module.scss";
import { Image } from "../../Section/Section";

const resolveLoanType = (param: string | null) => {
  switch (param) {
    case "serial":
      return LoanType.Serial;
    default:
      return LoanType.Annuity;
  }
};

interface ILoanCalculatorProps {
  image?: Image;
  headingContent: string;
  loanAmountSlider: ISlider;
  installmentFee: number;
  setupFee: number;
  repaymentPeriodSlider: ISlider;
  nominalInterestRateSlider: ISlider;
  loanType: "annuity" | "serial";
  priceExample?: string;
  useSlimLayout?: boolean;
}

const LoanCalculator: React.FC<ILoanCalculatorProps> = (props) => {
  const strings = useContext(StringContext);
  const settings = useContext(SettingsContext);

  const calculatorParams = {
    image: props.image,
    headingContent: props.headingContent,
    loanAmountSlider: props.loanAmountSlider,
    repaymentPeriodSlider: props.repaymentPeriodSlider,
    nominalInterestRateSlider: props.nominalInterestRateSlider,
    priceExample: props.priceExample,
  };

  const resolveProps = (props: ILoanCalculatorProps) => {
    const image = props.image;
    const headingContent = props.headingContent;
    const loanAmountSlider = props.loanAmountSlider;
    const repaymentPeriodSlider = props.repaymentPeriodSlider;
    const nominalInterestRateSlider = props.nominalInterestRateSlider;
    const priceExample = props.priceExample;

    const loanAmount = loanAmountSlider?.default ?? 0;
    const installmentFee = props.installmentFee ?? 0;
    const setupFee = props.setupFee ?? 0;
    const numberOfMonths = repaymentPeriodSlider?.default ? repaymentPeriodSlider.default * 12 : 1;
    const interestRate = nominalInterestRateSlider?.default ?? 0;
    const loanType = resolveLoanType(props.loanType);

    return {
      image,
      headingContent,
      loanAmountSlider,
      loanAmount,
      installmentFee,
      setupFee,
      repaymentPeriodSlider,
      numberOfMonths,
      nominalInterestRateSlider,
      interestRate,
      loanType,
      priceExample,
    };
  };

  const { loanAmount, installmentFee, setupFee, numberOfMonths, interestRate, loanType } = resolveProps(props);

  const theme = useTheme();

  const [loanParams, setLoanParams] = useState<LoanParams>({
    loanType: loanType,
    loanAmount: loanAmount,
    numberOfMonths: numberOfMonths,
    interestRate: interestRate,
    setupFee: setupFee,
    installmentFee: installmentFee,
    addFeeToLoanAmount: true,
  });

  const [calculateLoanResult, setcalculateLoanResult] = useState<CalculateLoanResult>();

  const [repaymentYears, setRepaymentYears] = useState<number>(numberOfMonths / 12);

  const updateInstallmentFee = (input: string) => {
    const anyValue = input.replace(" ", "") as any;
    if (!isNaN(anyValue)) {
      const installmentFee = Number(anyValue) || 0;
      setLoanParams({ ...loanParams, installmentFee });
    }
  };

  const updateSetupFee = (input: string) => {
    const anyValue = input.replace(" ", "") as any;
    if (!isNaN(anyValue)) {
      const setupFee = Number(anyValue) || 0;
      setLoanParams({ ...loanParams, setupFee });
    }
  };

  const setLoanAmount = (loanAmount: number) => {
    setLoanParams({ ...loanParams, loanAmount });
  };

  const setNumberOfYears = (numberOfYears: number) => {
    setLoanParams({ ...loanParams, numberOfMonths: numberOfYears * 12 });
    setRepaymentYears(numberOfYears);
  };

  const setInterestRate = (interestRate: number) => {
    setLoanParams({ ...loanParams, interestRate });
  };

  useEffect(() => {
    setcalculateLoanResult(calculateLoan(loanParams));
  }, [loanParams]);

  const { locale } = useContext(SettingsContext);

  const formatCurrency = (number: number | undefined) => {
    const region = settings.region;
    const cf = currencyFormatter();
    return cf(number ?? 0, region);
  };

  const formatNumber = (number: number | undefined, showDecimals: boolean | undefined = false) => {
    const region = settings.region;
    let locales: Culture | undefined = undefined;
    switch (region) {
      case RegionId.Norway:
        locales = "nb-NO";
        break;
      case RegionId.Sweden:
        locales = "sv-SE";
        break;
      case RegionId.Denmark:
        locales = "da-DK";
        break;
      case RegionId.Finland:
        locales = "fi-FI";
        break;
      case RegionId.Spain:
        locales = "es-ES";
        break;
      case RegionId.Germany:
        locales = "de-DE";
        break;
    }
    const nf = numberFormatter(locales);
    return nf(number ?? 0, showDecimals);
  };
  const sliderMarks = [] as any;
  const repaymentSliderMinValue = calculatorParams.repaymentPeriodSlider.min;
  const repaymentSliderMaxValue = calculatorParams.repaymentPeriodSlider.max;
  const repaymentSliderStepValue = calculatorParams.repaymentPeriodSlider.step;
  for (let i = repaymentSliderMinValue; i <= repaymentSliderMaxValue; i += repaymentSliderStepValue) {
    const mark = { value: i, label: i.toString() };
    sliderMarks.push(mark);
  }

  return (
    <Grid
      container
      sx={{
        maxWidth: "120rem",
        mx: "auto",
        marginTop: 2,
      }}
    >
      <Grid
        className={styles.gridItemLeft}
        size={{
          xs: 12,
          lg: 4,
        }}
      >
        <Paper sx={{ borderRadius: 10, marginTop: 1, m: { xs: 2, lg: 0 } }}>
          {calculatorParams.image && (
            <SectionWrapperImage imageHidden={{ smDown: true, smUp: false }}>
              <Media
                AltText={calculatorParams.image?.alt ?? ""}
                imagePath={calculatorParams.image?.url ?? ""}
                className={styles.imgStyleHero}
                noWrapper={true}
                aspectRatio="1:1"
              />
            </SectionWrapperImage>
          )}
          <LoanCalculatorSectionWrapper>
            <BNRichText html={calculatorParams.headingContent} />
            <div className={styles.sliderContainer} style={{ marginBottom: 24 }}>
              <SliderWithInput
                title={strings.loanCalculator_Loan}
                min={calculatorParams.loanAmountSlider.min}
                max={calculatorParams.loanAmountSlider.max}
                default={loanParams.loanAmount}
                unitSymbol={calculatorParams.loanAmountSlider.unitSymbol}
                ariaLabel={calculatorParams.loanAmountSlider.ariaLabel}
                step={calculatorParams.loanAmountSlider.step}
                adornmentPosition="start"
                size={SliderWithInputSizes.Large}
                centered={true}
                onChangeHandler={setLoanAmount}
              />
            </div>
            <div className={styles.inputContainer}>
              <div className={styles.inputColumnLeft}>
                <NumericFormat
                  value={loanParams.installmentFee}
                  thousandSeparator=" "
                  onChange={(e) => updateInstallmentFee(e.target.value)}
                  customInput={BNInput}
                  label={strings.loanCalculator_InstallmentFee}
                  isAllowed={(values: NumberFormatValues) => {
                    const floatValue = values.floatValue ?? 0;
                    return floatValue < 200;
                  }}
                  type="tel"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" disablePointerEvents={true}>
                        {calculatorParams.loanAmountSlider.unitSymbol}
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      textAlign: "end",
                      height: "2rem",
                    },
                  }}
                />
              </div>
              <div className={styles.inputColumnCenter}></div>
              <div className={styles.inputColumnRight}>
                <NumericFormat
                  value={loanParams.setupFee}
                  thousandSeparator=" "
                  onChange={(e) => updateSetupFee(e.target.value)}
                  customInput={BNInput}
                  label={strings.loanCalculator_SetupFee}
                  isAllowed={(values: NumberFormatValues) => {
                    const floatValue = values.floatValue ?? 0;
                    return floatValue >= 0 && floatValue < 5000;
                  }}
                  type="tel"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">{calculatorParams.loanAmountSlider.unitSymbol}</InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      textAlign: "end",
                      height: "2rem",
                    },
                  }}
                />
              </div>
            </div>
            {calculatorParams.repaymentPeriodSlider.min !== calculatorParams.repaymentPeriodSlider.max && (
              <>
                <div className={styles.inputContainer}>
                  <div className={styles.inputColumnLeft}>
                    <NumericFormat
                      value={repaymentYears}
                      thousandSeparator=" "
                      label={strings.loanCalculator_RepaymentPeriod}
                      onChange={(e) => setNumberOfYears(Number(e.target.value))}
                      customInput={BNInput}
                      isAllowed={(values: NumberFormatValues) => {
                        const floatValue = values.floatValue ?? 0;
                        return floatValue >= 0 && floatValue <= repaymentSliderMaxValue;
                      }}
                      type="tel"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {loanParams.numberOfMonths <= 12
                              ? strings.loanCalculator_year
                              : strings.loanCalculator_years}
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& legend": { display: "none" },
                        "& fieldset": { top: 0 },
                        "& .MuiInputBase-root": {
                          fontSize: 16,
                        },
                        "& .MuiInputBase-input": {
                          textAlign: "end",
                          height: "2rem",
                        },
                      }}
                    />
                  </div>
                  <div className={styles.inputColumnCenter}></div>
                  <div className={styles.inputColumnRight}></div>
                </div>
                <div className={styles.sliderContainer}>
                  <Slider
                    min={repaymentSliderMinValue}
                    max={repaymentSliderMaxValue}
                    default={repaymentYears}
                    ariaLabel={calculatorParams.repaymentPeriodSlider?.ariaLabel}
                    step={repaymentSliderStepValue}
                    marks={repaymentSliderMaxValue <= 10 ? sliderMarks : undefined}
                    hideCurrentValueLabel={true}
                    onChangeHandler={setNumberOfYears}
                  />
                </div>
              </>
            )}
            {calculatorParams.nominalInterestRateSlider.min !== calculatorParams.nominalInterestRateSlider.max && (
              <>
                <div className={styles.inputContainer}>
                  <div className={styles.inputColumnLeft}>
                    <NumericFormat
                      value={loanParams.interestRate}
                      thousandSeparator=" "
                      label={strings.loanCalculator_NominalInterest}
                      decimalSeparator=","
                      onChange={(e) => setInterestRate(Number(e.target.value.replace(",", ".")))}
                      customInput={BNInput}
                      isAllowed={(values: NumberFormatValues) => {
                        const floatValue = values.floatValue ?? 0;
                        return floatValue >= 0 && floatValue <= calculatorParams.nominalInterestRateSlider.max;
                      }}
                      type="tel"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      sx={{
                        "& legend": { display: "none" },
                        "& fieldset": { top: 0 },
                        "& .MuiInputBase-root": {
                          fontSize: 16,
                        },
                        "& .MuiInputBase-input": {
                          textAlign: "end",
                          height: "2rem",
                        },
                      }}
                    />
                  </div>
                  <div className={styles.inputColumnCenter}></div>
                  <div className={styles.inputColumnRight}></div>
                </div>
                <div className={styles.sliderContainer}>
                  <Slider
                    min={calculatorParams.nominalInterestRateSlider.min}
                    max={calculatorParams.nominalInterestRateSlider.max}
                    default={loanParams.interestRate}
                    ariaLabel={calculatorParams.nominalInterestRateSlider?.ariaLabel}
                    step={calculatorParams.nominalInterestRateSlider.step ?? 0}
                    decimals={2}
                    hideCurrentValueLabel={true}
                    onChangeHandler={setInterestRate}
                  />
                </div>
              </>
            )}
          </LoanCalculatorSectionWrapper>
        </Paper>
      </Grid>
      <Grid
        sx={{ paddingRight: { lg: 0 }, paddingTop: { lg: 0 } }}
        size={{
          xs: 12,
          lg: 8,
        }}
      >
        <BNBox
          className={`${styles.contentContainer}`}
          id="loan-calculator-settings"
          sx={{
            borderRadius: 10,
            backgroundColor: "tertiary.main",
          }}
          component={"section"}
        >
          <SectionWrapperText className={styles.repaymentPlanSectionWrapper}>
            <BNBox
              sx={[
                { fontSize: "2.4rem", display: "flex", color: "tertiary.contrastText" },
                theme.applyStyles("dark", { color: "text.primary" }),
              ]}
            >
              {strings.loanCalculator_Calculation}
            </BNBox>
            <Grid container className={styles.gridContainer}>
              <Grid
                className={styles.gridItem}
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <BNCard className={`${styles.card} ${styles.cardInvoice}`} sx={{ backgroundColor: "background.paper" }}>
                  <CardHeader className={styles.cardHeader} title={<h3>{strings.loanCalculator_FirstInvoice}</h3>} />
                  <CardContent>
                    <div className={`${styles.invoiceInfoRow} ${styles.invoiceInfoRowMain}`}>
                      <div>{strings.loanCalculator_AmountToPay}</div>
                      <div className={styles.invoiceInfoRowAmount}>
                        {formatCurrency(calculateLoanResult?.years[0]?.months[0]?.totalAmount)}
                      </div>
                    </div>
                    <div className={styles.invoiceInfoChartContainer}>
                      <div className={`${styles.invoiceInfoChartLegend} ${styles.invoiceInfoChartLegendLeft}`}>
                        {strings.loanCalculator_Payment}
                      </div>
                      <div className={styles.invoiceInfoChart}>
                        <BNDonutChart
                          data={[
                            {
                              value: calculateLoanResult?.years[0]?.months[0]?.interestAmount || 0,
                              label: strings.loanCalculator_Interest,
                            },
                            {
                              value: calculateLoanResult?.years[0]?.months[0]?.repaymentAmount || 0,
                              label: strings.loanCalculator_Payment,
                            },
                          ]}
                          colors={["var(--mui-palette-primary-dark)", "var(--mui-palette-text-primary)"]}
                          strokeColor="#ffffff"
                          clickToggle={false}
                          width={104}
                          innerRadius={0.7}
                          outerRadius={0.9}
                        />
                      </div>
                      <div className={`${styles.invoiceInfoChartLegend} ${styles.invoiceInfoChartLegendRight}`}>
                        {strings.loanCalculator_Interest}
                      </div>
                    </div>
                    <div className={`${styles.invoiceInfoRow} ${styles.invoiceInfoRowBottom}`}>
                      <div>{strings.loanCalculator_TotalAmount}</div>
                      <div className={styles.invoiceInfoRowAmount}>{formatCurrency(calculateLoanResult?.sum)}</div>
                    </div>
                    <p className={styles.explanation}>
                      {loanParams.loanType === LoanType.Serial
                        ? strings.loanCalculator_SerialText
                        : strings.loanCalculator_AnnuityText}
                    </p>
                  </CardContent>
                </BNCard>
              </Grid>
              <Grid
                className={styles.gridItem}
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <BNCard className={`${styles.card} ${styles.cardSummary}`} sx={{ backgroundColor: "background.paper" }}>
                  <CardHeader className={styles.cardHeader} title={<h3>{strings.loanCalculator_Summary}</h3>} />
                  <CardContent>
                    <div className={`${styles.cardInfoRow} ${styles.cardInfoRowSpaceBelow}`}>
                      <div>{strings.loanCalculator_Loan}</div>
                      <div className={styles.cardInfoRowAmount}>{formatCurrency(loanParams.loanAmount)}</div>
                    </div>
                    <div className={`${styles.cardInfoRow}`}>
                      <div>{strings.loanCalculator_InstallmentFee}</div>
                      <div className={styles.cardInfoRowAmount}>{formatCurrency(loanParams.installmentFee)}</div>
                    </div>
                    <div className={`${styles.cardInfoRow} ${styles.cardInfoRowSpaceBelow}`}>
                      <div>{`${strings.loanCalculator_SetupFee}`}</div>
                      <div className={styles.cardInfoRowAmount}>{formatCurrency(loanParams.setupFee)}</div>
                    </div>

                    <div className={`${styles.cardInfoRow}`}>
                      <div>{`${strings.loanCalculator_EffInterest}`}</div>
                      <div className={styles.cardInfoRowAmount}>
                        {`${formatNumber(calculateLoanResult?.effectiveInterest, true)}%`}
                      </div>
                    </div>
                    <div className={`${styles.cardInfoRow} ${styles.cardInfoRowSpaceBelow}`}>
                      <div>{strings.loanCalculator_PeriodInterestCost}</div>
                      <div className={styles.cardInfoRowAmount}>{formatCurrency(calculateLoanResult?.interestSum)}</div>
                    </div>
                    <div className={`${styles.cardInfoRow} ${styles.cardInfoRowTotal}`}>
                      <div>{strings.loanCalculator_TotalAmount}</div>
                      <div className={styles.cardInfoRowAmount}>{formatCurrency(calculateLoanResult?.sum)}</div>
                    </div>
                    <div className={styles.explanation}>
                      <RichText html={calculatorParams.priceExample ?? strings.loanCalculator_ExampleText} />
                    </div>
                  </CardContent>
                </BNCard>
              </Grid>
            </Grid>
          </SectionWrapperText>
        </BNBox>
        <BNBox
          className={`${styles.contentContainer}`}
          id="loan-calculator-repaymentplan"
          sx={{
            borderRadius: 10,
            marginTop: 2,
            backgroundColor: "tertiary.main",
          }}
          component={"section"}
        >
          <SectionWrapperText className={styles.repaymentPlanSectionWrapper}>
            <div className={styles.repaymentPlanAccordionHeaderContainer}>
              <div className={styles.repaymentPlanAccordionHeaderText}>
                <BNBox
                  sx={[
                    {
                      fontSize: "2.4rem",
                      display: "flex",
                      color: "tertiary.contrastText",
                    },
                    theme.applyStyles("dark", { color: "text.primary" }),
                  ]}
                >
                  <span>
                    {strings.loanCalculator_RepaymentPlan}{" "}
                    <b>
                      {new Intl.NumberFormat(locale, {
                        maximumFractionDigits: 2,
                      }).format(loanParams.numberOfMonths / 12)}{" "}
                      {Math.ceil(loanParams.numberOfMonths / 12) > 1
                        ? strings.loanCalculator_years
                        : strings.loanCalculator_year}
                    </b>
                  </span>
                </BNBox>
              </div>
            </div>
            {calculateLoanResult && (
              <RepaymentPlan
                repaymentPlan={calculateLoanResult.years}
                strings={strings}
                formatter={formatCurrency}
                isActive={true}
              />
            )}
          </SectionWrapperText>
        </BNBox>
      </Grid>
    </Grid>
  );
};

export default LoanCalculator;
